import * as THREE from 'three';
import { fbm } from '../look/noise.js';
import { polar } from './terrain.js';

/**
 * Surf (the island alive, Pieter 28 Sep 2026): a band of foam where the sea
 * meets the shore -- the island's own, the volcano's foot, the islet -- lying
 * just above the water, rolling in and fading out in its own shader. One mesh,
 * one draw call, a few thousand triangles.
 */

/** A shore as points (x, z) along it, in order; closed when it goes all the way round. */
export type Shore = {
  readonly points: readonly (readonly [number, number])[];
  readonly closed: boolean;
};

/**
 * Where the land at `height` meets the sea at `level`, looking out from the
 * origin: per direction, the first place the ground goes under water, found to
 * a few centimetres. Directions with no shore within `reach` break it into arcs.
 */
export function shoreline(
  height: (x: number, z: number) => number,
  level: number,
  options: { segments: number; from?: number; reach?: number; maxStep?: number },
): Shore[] {
  const from = options.from ?? 10;
  const reach = options.reach ?? 450;
  const found: ([number, number] | undefined)[] = [];
  for (let j = 0; j < options.segments; j++) {
    const a = (j / options.segments) * Math.PI * 2;
    const under = (r: number): boolean => {
      const [x, z] = polar(a, r);
      return height(x, z) < level;
    };
    let hit: number | undefined;
    for (let r = from; r <= reach; r += 1) {
      if (under(r)) {
        let lo = r - 1;
        let hi = r;
        for (let k = 0; k < 12; k++) {
          const mid = (lo + hi) / 2;
          if (under(mid)) hi = mid;
          else lo = mid;
        }
        hit = (lo + hi) / 2;
        break;
      }
    }
    found.push(hit === undefined ? undefined : polar(a, hit));
  }
  // A step longer than maxStep is a jump from one shore to another (a bay, a
  // headland behind): no band may span it, so it breaks the shore like a gap.
  const maxStep = options.maxStep ?? 12;
  const breaks = found.map((point, j) => {
    const before = found[(j - 1 + found.length) % found.length];
    return (
      point === undefined ||
      (before !== undefined && Math.hypot(point[0] - before[0], point[1] - before[1]) > maxStep)
    );
  });
  if (!breaks.some(Boolean)) {
    return [{ points: found as [number, number][], closed: true }];
  }
  // Arcs between the breaks, starting at one so none is split at angle 0.
  const start = breaks.findIndex(Boolean);
  const arcs: Shore[] = [];
  let arc: [number, number][] = [];
  for (let k = 0; k < found.length; k++) {
    const j = (start + k) % found.length;
    const point = found[j];
    if (breaks[j] && arc.length > 0) {
      if (arc.length > 1) arcs.push({ points: arc, closed: false });
      arc = [];
    }
    if (point) arc.push(point);
  }
  if (arc.length > 1) arcs.push({ points: arc, closed: false });
  return arcs;
}

/** A round shore: a ring of `segments` points `radius` from `centre`. */
export function ringShore(
  centre: readonly [number, number],
  radius: number,
  segments: number,
): Shore {
  const points: [number, number][] = [];
  for (let j = 0; j < segments; j++) {
    const [x, z] = polar((j / segments) * Math.PI * 2, radius);
    points.push([centre[0] + x, centre[1] + z]);
  }
  return { points, closed: true };
}

/** How far the band reaches onto the land, and out onto the sea, in metres. */
const LANDWARD = 1.5;
const SEAWARD = 8;

const vertexShader = /* glsl */ `
#include <common>
#include <fog_pars_vertex>
attribute float across;
attribute float along;
attribute vec3 rise;
uniform float uTide;
varying float vAcross;
varying float vAlong;
void main() {
  vAcross = across;
  vAlong = along;
  vec4 mvPosition = modelViewMatrix * vec4(position + rise * uTide, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

const fragmentShader = /* glsl */ `
#include <common>
#include <fog_pars_fragment>
uniform float uTime;
uniform sampler2D uNoise;
varying float vAcross;
varying float vAlong;
void main() {
  float lace = texture2D(uNoise, vec2(vAlong / 23.0, vAcross * 0.7 - uTime * 0.03)).r;
  // Lines of foam rolling in towards the shore, broken along it.
  float wave = fract(vAcross * 1.6 + uTime * 0.18 + lace * 0.6);
  float line = smoothstep(0.72, 0.95, wave) * smoothstep(0.35, 0.6, lace + 0.15);
  // Always a wash at the waterline, broken up by the lace.
  float wash = (1.0 - smoothstep(0.1, 0.28, vAcross)) * smoothstep(0.25, 0.55, lace + 0.2);
  float foam = max(line * (1.0 - smoothstep(0.5, 1.0, vAcross)), wash);
  // Soft on its landward edge, gone at its seaward one.
  foam *= smoothstep(0.0, 0.06, vAcross);
  gl_FragColor = vec4(vec3(1.0), foam * 0.9);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}
`;

function noiseTexture(size = 64): THREE.DataTexture {
  const noise = fbm(57, 4, 8);
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const v = Math.round(noise((x / size) * 8, (y / size) * 8) * 255);
      data.set([v, v, v, 255], (y * size + x) * 4);
    }
  }
  const texture = new THREE.DataTexture(data, size, size);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

/**
 * How far each shore point moves when the water rises from `low` to `high` (a tide): landward, against
 * the shore's seaward normal, to where the ground meets the higher water; found in 25 cm steps and bisected, at most
 * 200 m. One [dx, dy, dz] per point, per shore.
 */
export function shoreRise(
  shores: readonly Shore[],
  ground: (x: number, z: number) => number,
  low: number,
  high: number,
): (readonly [number, number, number])[][] {
  return shores.map((shore) => {
    const n = shore.points.length;
    return shore.points.map(([x, z], j) => {
      const [px, pz] = shore.points[(j - 1 + n) % n]!;
      const [nx, nz] = shore.points[(j + 1) % n]!;
      const prev = shore.closed || j > 0 ? [px, pz] : [x, z];
      const next = shore.closed || j < n - 1 ? [nx, nz] : [x, z];
      let ox = next[1]! - prev[1]!;
      let oz = -(next[0]! - prev[0]!);
      const length = Math.hypot(ox, oz) || 1;
      ox /= length;
      oz /= length;
      // In 0.25 m steps to the first place the ground is at the higher water, then bisected to under a
      // millimetre: at most ~810 samples a point (a package's ground is a function it cannot make slow).
      const under = (d: number): boolean => ground(x - ox * d, z - oz * d) < high;
      let d = 0;
      while (d < 200 && under(d)) d += 0.25;
      if (d > 0 && d < 200) {
        let lo = d - 0.25;
        let hi = d;
        for (let k = 0; k < 8; k++) {
          const mid = (lo + hi) / 2;
          if (under(mid)) lo = mid;
          else hi = mid;
        }
        d = hi;
      }
      return [-ox * d, high - low, -oz * d] as const;
    });
  });
}

/**
 * The surf along every shore, as one mesh. `rise` (per shore, per point: see shoreRise) is how far a
 * point moves when the tide is in (`setTide(1)`); without it the surf stays where it is.
 */
export function buildSurf(
  shores: readonly Shore[],
  level: number,
  rise?: readonly (readonly (readonly [number, number, number])[])[],
): { mesh: THREE.Mesh; update(elapsed: number): void; setTide(tide: number): void } {
  const positions: number[] = [];
  const across: number[] = [];
  const along: number[] = [];
  const rises: number[] = [];
  let longest = 0;
  const index: number[] = [];
  const y = level + 0.06;
  for (const [s, shore] of shores.entries()) {
    const n = shore.points.length;
    const base = positions.length / 3;
    let run = 0;
    for (let j = 0; j < n; j++) {
      const [x, z] = shore.points[j]!;
      // Out to sea: the shore's normal. Every shore here is walked as polar()
      // turns (the angle growing), for which (dz, −dx) points away from the land.
      const [px, pz] = shore.points[(j - 1 + n) % n]!;
      const [nx, nz] = shore.points[(j + 1) % n]!;
      const prev = shore.closed || j > 0 ? [px, pz] : [x, z];
      const next = shore.closed || j < n - 1 ? [nx, nz] : [x, z];
      let ox = next[1]! - prev[1]!;
      let oz = -(next[0]! - prev[0]!);
      const length = Math.hypot(ox, oz) || 1;
      ox /= length;
      oz /= length;
      if (j > 0) run += Math.hypot(x - shore.points[j - 1]![0], z - shore.points[j - 1]![1]);
      positions.push(
        x - ox * LANDWARD,
        y,
        z - oz * LANDWARD,
        x + ox * SEAWARD,
        y,
        z + oz * SEAWARD,
      );
      const r = rise?.[s]?.[j] ?? [0, 0, 0];
      rises.push(...r, ...r);
      longest = Math.max(longest, Math.hypot(...r));
      across.push(0, 1);
      along.push(run, run);
    }
    const segments = shore.closed ? n : n - 1;
    for (let j = 0; j < segments; j++) {
      const a = base + j * 2;
      const b = base + ((j + 1) % n) * 2;
      index.push(a, b, a + 1, b, b + 1, a + 1);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('across', new THREE.Float32BufferAttribute(across, 1));
  geometry.setAttribute('along', new THREE.Float32BufferAttribute(along, 1));
  geometry.setAttribute('rise', new THREE.Float32BufferAttribute(rises, 3));
  geometry.setIndex(index);
  geometry.computeBoundingSphere();
  // A risen surf is never culled: the sphere grows by the longest rise.
  if (geometry.boundingSphere) geometry.boundingSphere.radius += longest;
  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    fog: true,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    uniforms: THREE.UniformsUtils.merge([
      THREE.UniformsLib.fog,
      { uTime: { value: 0 }, uTide: { value: 0 }, uNoise: { value: null } },
    ]),
  });
  material.uniforms['uNoise']!.value = noiseTexture();
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'surf';
  mesh.raycast = (): void => {};
  return {
    mesh,
    update(elapsed) {
      material.uniforms['uTime']!.value = elapsed;
    },
    setTide(tide) {
      material.uniforms['uTide']!.value = tide;
    },
  };
}
